import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';
import { Outlet, useLocation, useNavigate } from 'react-router';
import { shallow } from '@jwp/ott-common/src/utils/compare';
import { getModule } from '@jwp/ott-common/src/modules/container';
import { useAccountStore } from '@jwp/ott-common/src/stores/AccountStore';
import { useUIStore } from '@jwp/ott-common/src/stores/UIStore';
import { useConfigStore } from '@jwp/ott-common/src/stores/ConfigStore';
import { useProfileStore } from '@jwp/ott-common/src/stores/ProfileStore';
import ProfileController from '@jwp/ott-common/src/controllers/ProfileController';
import { modalURLFromLocation } from '@jwp/ott-ui-react/src/utils/location';
import { isTruthyCustomParamValue, unicodeToChar } from '@jwp/ott-common/src/utils/common';
import { ACCESS_MODEL } from '@jwp/ott-common/src/constants';
import useSearchQueryUpdater from '@jwp/ott-ui-react/src/hooks/useSearchQueryUpdater';
import { useProfiles, useSelectProfile } from '@jwp/ott-hooks-react/src/useProfiles';
import useOpaqueId from '@jwp/ott-hooks-react/src/useOpaqueId';
import { PATH_HOME, PATH_USER_PROFILES } from '@jwp/ott-common/src/paths';
import { determinePath } from '@jwp/ott-common/src/utils/urlFormatting';
import env from '@jwp/ott-common/src/env';
import AccountController from '@jwp/ott-common/src/controllers/AccountController';
import Cookies from 'js-cookie';
import { fetchSSOAccessToken, signInUser } from '@jwp/ott-common/src/controllers/graphql';
import LoadingOverlay from '../../components/LoadingOverlay/LoadingOverlay';
import ErrorPage from '../../components/ErrorPage/ErrorPage';

import { useMutation } from 'react-query';

import { getSiteType } from '../../utils/useSiteType';
import Header from '../../components/Header/Header';
import Sidebar from '../../components/Sidebar/Sidebar';
import MenuButton from '../../components/MenuButton/MenuButton';
import UserMenu from '../../components/UserMenu/UserMenu';
import Button from '../../components/Button/Button';
import Footer from '../../components/Footer/Footer';

import styles from './Layout.module.scss';

const Layout = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation('common');
  const [siteType, setSiteType] = useState<string>('');
  const accountController = getModule(AccountController);
  const [isSSOLogin, setIsSSOLogin] = useState(false);
  const { loading } = useAccountStore.getState();

  const { config, accessModel, supportedLanguages } = useConfigStore(
    ({ config, accessModel, supportedLanguages }) => ({ config, accessModel, supportedLanguages }),
    shallow,
  );
  const userMenuTitleId = useOpaqueId('usermenu-title');
  const isLoggedIn = !!useAccountStore(({ user }) => user);
  const user = useAccountStore((state) => state.user);
  const { metadata } = user ?? {};
  const { IsLibraryOnboarded } = metadata ?? {};
  const favoritesEnabled = !!config.features?.favoritesList;
  const { menu, assets, siteName, description, features, styling, custom } = config;
  const metaDescription = description || t('default_description');
  const { footerText: configFooterText } = styling || {};
  const footerText = configFooterText || unicodeToChar(env.APP_FOOTER_TEXT);

  const profileController = getModule(ProfileController, false);

  const { searchPlaylist } = features || {};
  const hasAppContentSearch = isTruthyCustomParamValue(custom?.appContentSearch);
  const searchEnabled = !!searchPlaylist || hasAppContentSearch;
  const currentLanguage = useMemo(() => supportedLanguages.find(({ code }) => code === i18n.language), [i18n.language, supportedLanguages]);

  const {
    query: { data: { collection: profiles = [] } = {} },
    profilesEnabled,
  } = useProfiles();

  const selectProfile = useSelectProfile({
    onSuccess: () => navigate(PATH_HOME),
    onError: () => navigate(PATH_USER_PROFILES),
  });

  useEffect(() => {
    const siteType = getSiteType();
    setSiteType(siteType);
  }, []);

  const getAccessTokenMutation = useMutation({
    mutationFn: fetchSSOAccessToken,
    onError: (error: any) => {
      navigate(modalURLFromLocation(location, 'sso-invalid-token'), {
        state: { message: error.message },
      });
      setIsSSOLogin(false);
    },
    onSuccess: (data: any) => {
      if (data?.validateTicket?.access_token) {
        const ssoAccessToken = data?.validateTicket?.access_token;
        const tokenData = {
          token: data?.validateTicket?.access_token,
          refreshToken: '',
          expires: data?.validateTicket?.expires,
        };
        const authData = Cookies.get('authData');
        if (data?.validateTicket?.access_token) {
          if (authData) {
            setIsSSOLogin(false);
            navigate(modalURLFromLocation(location, 'sso-user-detect'), {
              state: { tokenData },
            });
          } else {
            const tokenData = {
              token: data?.validateTicket?.access_token,
              refreshToken: '',
              expires: data?.validateTicket?.expires,
            };
            localStorage.setItem('inplayer_token', JSON.stringify(tokenData));
            const response = accountController.ssoLogin(tokenData);
            if (response) {
              navigate('/');
              setIsSSOLogin(false);
            }
            setIsSSOLogin(false);
          }
        }
      }
    },
  });

  function parseJWTtoken(token: string) {
    const arrayToken = token.split('.');
    try {
      return JSON.parse(atob(arrayToken[1]));
    } catch (err) {
      navigate(modalURLFromLocation(location, 'sso-token-invalid'));
      console.error('Invalid token:', err);
      return null;
    }
  }

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const token = params.get('accessToken');

    const siteType = getSiteType();
    const handleSSOLogin = async () => {
      if (token) {
        setIsSSOLogin(true);

        const parsedToken = parseJWTtoken(token);

        if (!parsedToken || !parsedToken.exp) {
          console.error('Failed to parse token');
          setIsSSOLogin(false);
          // navigate(modalURLFromLocation(location, 'sso-token-invalid'));
          // navigate('/');
          return;
        }

        const tokenData = {
          token: token,
          refreshToken: '',
          expires: parsedToken.exp,
        };

        const authData = Cookies.get('authData');

        if (authData) {
          setIsSSOLogin(false);
          navigate(modalURLFromLocation(location, 'sso-user-detect'), {
            state: { tokenData },
          });
        } else {
          localStorage.setItem('inplayer_token', JSON.stringify(tokenData));
          try {
            const response = await accountController.ssoLogin(tokenData);

            navigate('/');
          } catch (error) {
            navigate(modalURLFromLocation(location, 'sso-login-error'), {
              state: { error },
            });
            console.error('SSO login failed:', error);
          } finally {
            setIsSSOLogin(false);
          }
        }
      }
    };

    if (siteType === 'academic') {
      handleSSOLogin();
    }
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const ticket = params.get('ticket');

    const siteType = getSiteType();
    if (siteType !== 'academic') {
      return;
    }

    if (ticket) {
      setIsSSOLogin(true);
      getAccessTokenMutation.mutate(ticket);
    }
  }, []);

  const { searchQuery, searchActive, userMenuOpen, languageMenuOpen } = useUIStore(
    ({ searchQuery, searchActive, userMenuOpen, languageMenuOpen }) => ({
      languageMenuOpen,
      searchQuery,
      searchActive,
      userMenuOpen,
    }),
    shallow,
  );
  const { updateSearchQuery, resetSearchQuery } = useSearchQueryUpdater();
  const { profile } = useProfileStore();

  const searchInputRef = useRef<HTMLInputElement>(null) as React.MutableRefObject<HTMLInputElement>;

  const [sideBarOpen, setSideBarOpen] = useState(false);
  const banner = assets.banner;
  const canLogin = accessModel !== ACCESS_MODEL.AVOD;

  useEffect(() => {
    if (isLoggedIn && profilesEnabled && !profiles?.length) {
      profileController?.unpersistProfile();
    }
    // Trigger once on the initial page load
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    document.documentElement.setAttribute('lang', i18n.language);
  }, [i18n.language]);

  useEffect(() => {
    if (searchActive && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [searchActive]);

  const searchButtonClickHandler = () => {
    useUIStore.setState({
      searchActive: true,
      preSearchPage: `${location.pathname}${location.search || ''}`,
    });
  };

  const closeSearchButtonClickHandler = () => {
    resetSearchQuery();

    useUIStore.setState({
      searchActive: false,
    });
  };

  const loginButtonClickHandler = () => {
    navigate(modalURLFromLocation(location, 'login'));
  };

  const signUpButtonClickHandler = () => {
    navigate(modalURLFromLocation(location, 'create-account'));
  };

  const languageClickHandler = async (code: string) => {
    await i18n.changeLanguage(code);
    localStorage.setItem('jwapp.language', code);
  };

  // useCallbacks are used here to fix a bug in the Popover when using a Reactive onClose callback
  const openUserPanel = useCallback(() => useUIStore.setState({ userMenuOpen: true }), []);
  const closeUserPanel = useCallback(() => useUIStore.setState({ userMenuOpen: false }), []);
  const openLanguageMenu = useCallback(() => useUIStore.setState({ languageMenuOpen: true }), []);
  const closeLanguageMenu = useCallback(() => useUIStore.setState({ languageMenuOpen: false }), []);

  const renderUserActions = (sideBarOpen: boolean) => {
    if (!canLogin) return null;

    return isLoggedIn ? (
      <section aria-labelledby={userMenuTitleId}>
        <UserMenu focusable={sideBarOpen} favoritesEnabled={favoritesEnabled} titleId={userMenuTitleId} showPaymentsItem />
      </section>
    ) : (
      <div className={styles.buttonContainer}>
        {/* <Button tabIndex={sideBarOpen ? 0 : -1} onClick={loginButtonClickHandler} label={t('sign_in')} fullWidth /> */}
        <Button tabIndex={sideBarOpen ? 0 : -1} variant="contained" color="primary" onClick={signUpButtonClickHandler} label={t('sign_up')} fullWidth />
      </div>
    );
  };

  // //If user close the app before completing Library access, reopening app should be landed in library access screen.
  const hasNavigatedRef = useRef(false);

  useEffect(() => {
    const isLibrary = sessionStorage.getItem('isSignInProgress') === 'true';
    const hasLibraryData = localStorage.getItem('SelectedLibraryData');

    const shouldNavigate =
      canLogin && IsLibraryOnboarded === 'false' && siteType === 'public' ? true : (user?.firstName?.length ?? 0) > 1 && !isLibrary && !hasLibraryData;

    if (!hasNavigatedRef.current && shouldNavigate) {
      hasNavigatedRef.current = true;
      // navigate(modalURLFromLocation(location, 'Library-auto-complete'));
    }
  }, [canLogin, IsLibraryOnboarded, user?.firstName, user?.lastName, siteType, location, navigate]);

  const containerProps = { inert: sideBarOpen ? '' : undefined }; // inert is not yet officially supported in react

  return (
    <div className={styles.layout}>
      <Helmet>
        <title>{siteName}</title>
        <meta name="description" content={metaDescription} />
        <meta property="og:description" content={metaDescription} />
        <meta property="og:title" content={siteName} />
        <meta name="twitter:title" content={siteName} />
        <meta name="twitter:description" content={metaDescription} />
      </Helmet>
      <div {...containerProps}>
        <Header
          onMenuButtonClick={() => setSideBarOpen(true)}
          logoSrc={banner}
          searchEnabled={searchEnabled}
          searchBarProps={{
            query: searchQuery,
            onQueryChange: (event) => updateSearchQuery(event.target.value),
            onClearButtonClick: () => updateSearchQuery(''),
            inputRef: searchInputRef,
          }}
          searchActive={searchActive}
          onSearchButtonClick={searchButtonClickHandler}
          onCloseSearchButtonClick={closeSearchButtonClickHandler}
          onLoginButtonClick={loginButtonClickHandler}
          onSignUpButtonClick={signUpButtonClickHandler}
          onLanguageClick={languageClickHandler}
          supportedLanguages={supportedLanguages}
          currentLanguage={currentLanguage}
          isLoggedIn={isLoggedIn}
          sideBarOpen={sideBarOpen}
          userMenuOpen={userMenuOpen}
          languageMenuOpen={languageMenuOpen}
          openUserPanel={openUserPanel}
          closeUserPanel={closeUserPanel}
          openLanguageMenu={openLanguageMenu}
          closeLanguageMenu={closeLanguageMenu}
          canLogin={canLogin}
          showPaymentsMenuItem={accessModel !== ACCESS_MODEL.AVOD}
          favoritesEnabled={favoritesEnabled}
          profilesData={{
            currentProfile: profile,
            profiles,
            profilesEnabled,
            selectProfile: ({ avatarUrl, id }) => selectProfile.mutate({ id, avatarUrl }),
            isSelectingProfile: selectProfile.isLoading,
          }}
        >
          <Button activeClassname={styles.headerButton} label={t('home')} to="/" variant="text" />
          {menu.map((item) => (
            <Button
              activeClassname={styles.headerButton}
              key={item.contentId}
              label={item.label}
              to={determinePath({ type: item.type, contentId: item.contentId, label: item.label })}
              variant="text"
            />
          ))}
        </Header>
        <main id="content" className={styles.main} tabIndex={-1}>
          <Outlet />
        </main>
      </div>
      {!!footerText && <Footer text={footerText} />}
      <Sidebar isOpen={sideBarOpen} onClose={() => setSideBarOpen(false)}>
        <ul>
          <li>
            <MenuButton label={t('home')} to="/" />
          </li>
          {menu.map((item) => (
            <li key={item.contentId}>
              <MenuButton label={item.label} to={determinePath({ type: item.type, contentId: item.contentId, label: item.label })} />
            </li>
          ))}
        </ul>
        {renderUserActions(sideBarOpen)}
      </Sidebar>
      {isSSOLogin && <LoadingOverlay />}
    </div>
  );
};

export default Layout;
